<template>
    <form :id="id" @submit.prevent="submit" novalidate>
        <slot />
    </form>
</template>

<script lang="ts">
import { computed, defineComponent, provide, ref, watch } from 'vue';
import { apiFieldErrors } from '~/api/queryUtils';
import { toast } from '~/composables/useToast';
import { Validator } from '~/core';
import useService from '~/plugins/service';

export default defineComponent({
    name: 'WispForm',

    props: {
        id: {
            type: String,
        },
        global: {
            type: Boolean,
        },
        serviceId: {
            type: [Function, String],
            required: true,
        },
        onSuccess: {
            type: [Function, String],
        },
        onError: {
            type: Function,
        },
        canSubmit: {
            type: Boolean,
            default: true,
        },
    },

    setup(props) {
        // VueJS v3 doesn't allow us to easily grab all slot's components and their set value
        // (other than by touching the virtual DOM, without access to component's methods).
        // Therefore we'll need to use the provide/inject to implement some type of workaround
        // to allow custom validation and getting the actual form data.
        let formComponents: FormComponent[] = [];

        // Bumped on (un)register so the live-values computed below re-runs when the
        // set of fields changes (the array itself is intentionally non-reactive to
        // avoid Vue auto-unwrapping each component's `value` ref).
        const registrationVersion = ref(0);

        provide('registerFormComponent', (data: FormComponent) => {
            formComponents.push(data);
            registrationVersion.value++;

            return () => {
                formComponents = formComponents.filter((a) => a !== data);
                registrationVersion.value++;
            };
        });

        // Opt-in reactive snapshot of every field's current value, keyed by `name`.
        // Consumers inject this to react to live form state (e.g. divergence checks).
        provide(
            'formValues',
            computed<Record<string, unknown>>(() => {
                registrationVersion.value;

                const values: Record<string, unknown> = {};
                formComponents.forEach((component) => {
                    if (component.key) {
                        values[component.key] = component.value?.value;
                    }
                });

                return values;
            }),
        );

        const displayFormErrors = () => {
            toast.dismiss();

            const errors = formComponents
                .map((component) => component.errors?.value)
                .filter((errors) => errors && errors.length > 0)
                .flat()
                .filter((error): error is TranslatableMessage => error !== null && error !== undefined);
            if (errors.length > 0 && props.global) {
                toast.error(['components.form.validation_error', errors.length], errors);
            }

            return errors.length;
        };
        provide('displayFormErrors', () => displayFormErrors());

        provide('formGlobal', props.global);

        const canSubmit = ref<boolean>(props.canSubmit);
        provide('formCanSubmit', canSubmit);
        watch(
            () => props.canSubmit,
            (value) => (canSubmit.value = value),
        );

        /**
         * Puts each of the API's validation messages back on the field it came
         * from. The submit handler has already toasted the failure; this is
         * what makes the offending input say why it was rejected.
         */
        const applyApiFieldErrors = (error: unknown) => {
            const fields = apiFieldErrors(error);

            formComponents.forEach((component) => {
                const messages = component.key ? fields[component.key] : undefined;
                if (!messages || !component.errors) return;

                component.errors.value = messages.map((message) => ['_raw', message] as TranslatableMessage);
            });
        };

        const submit = () => {
            if (!canSubmit.value) return;

            // Drop the previous response's messages first: `validate` recomputes
            // the client-side ones below, but it no-ops on fields without a rule,
            // which would otherwise keep showing a stale server error forever.
            formComponents.forEach((component) => {
                if (component.errors) component.errors.value = null;
            });

            formComponents.forEach((component) => component.validate?.());
            if (displayFormErrors() > 0) return;

            const onSubmit = (state: boolean) => formComponents.forEach((component) => component.onSubmit?.(state));
            onSubmit(true);

            const onSuccess = (data: any) => {
                formComponents.forEach((component) => component.onSuccess?.(data));

                if (props.onSuccess) {
                    if (typeof props.onSuccess === 'string') {
                        toast.success([props.onSuccess]);
                    } else {
                        props.onSuccess(data);
                    }
                }
            };

            const formData: Record<string, any> = {};
            formComponents.forEach((component) => {
                if (!component.key || !component.value) return;

                const split = component.key!.split('.');
                const finalKey = split.pop()!; // just assume this always exists

                // We can assume this will succeed as all inputs are validated above.
                const value = component.rule
                    ? Validator.validate(component.key, component.value!.value, component.rule).normalized
                    : component.value.value;

                split.reduce((data, key) => (data[key] = data[key] || {}), formData)[finalKey] = value;
            });

            if (props.serviceId instanceof Function) {
                const output = props.serviceId(formData);
                if (output instanceof Promise) {
                    output
                        .then((res) => onSuccess(res))
                        // Function serviceIds surface the failure themselves (a
                        // toast); all that is left here is attributing whatever
                        // the API blamed on a specific field back to that field.
                        .catch((error: unknown) => applyApiFieldErrors(error))
                        .finally(() => onSubmit(false));
                } else {
                    onSubmit(false);
                }
            } else {
                useService(props.serviceId, props.onError == null, formData)
                    .then((res) => onSuccess(res))
                    .catch((err) => {
                        if (props.onError) {
                            props.onError(err);
                        } else {
                            throw err;
                        }
                    })
                    .finally(() => onSubmit(false));
            }
        };
        provide('formSubmit', () => submit());

        return {
            submit,
        };
    },
});
</script>
